import {
  createEndEffector,
  createTip,
  deleteEndEffector,
  deleteTip,
  listEndEffectors,
  listTips,
  updateEndEffector,
  updateTip,
  type EmbodimentMutationOptions,
  type EndEffector,
  type PageQuery,
  type PageResp,
  type Tip,
} from "@/api";
import { normalizeOptionalText } from "@/utils";

// embodiment 의 구성 부품 종류. Embodiment 자체는 별도 탭(EmbodimentCatalogTab)에서 다룬다.
export type PartKind = "endEffector" | "tip";

// 부품을 한 테이블과 폼에서 다루기 위한 정규화 표시 레코드.
export type PartRecord = {
  uuid: string;
  name: string;
  tag: string | null;
  description: string | null;
  created_at: string;
  modified_at: string;
};

// 폼 입력값.
export type PartFormValues = {
  name: string;
  tag: string;
  description: string;
};

export type PartKindMeta = {
  kind: PartKind;
  singular: string;
  plural: string;
  hasTag: boolean;
};

export const partKindOrder: readonly PartKind[] = ["endEffector", "tip"];

export function isPartKind(value: string): value is PartKind {
  return (partKindOrder as readonly string[]).includes(value);
}

export const partKindMetas: Record<PartKind, PartKindMeta> = {
  endEffector: {
    kind: "endEffector",
    singular: "End effector",
    plural: "End effectors",
    hasTag: true,
  },
  tip: { kind: "tip", singular: "Tip", plural: "Tips", hasTag: true },
};

type PartItem = EndEffector | Tip;

// 부품 응답을 정규화 레코드로 바꾼다.
export function toPartRecord(item: PartItem): PartRecord {
  return {
    uuid: item.uuid,
    name: item.name,
    tag: "tag" in item ? item.tag : null,
    description: item.description,
    created_at: item.created_at,
    modified_at: item.modified_at,
  };
}

// 빈 폼 초기값. 편집할 때는 레코드에서 채운다.
export function emptyPartFormValues(): PartFormValues {
  return { name: "", tag: "", description: "" };
}

export function partRecordToFormValues(record: PartRecord): PartFormValues {
  return {
    name: record.name,
    tag: record.tag ?? "",
    description: record.description ?? "",
  };
}

// PartRecord 단위로 list/get/create/update/remove 를 노출하는 종류별 어댑터.
export type PartApi = {
  list: (query: PageQuery, options?: { signal?: AbortSignal }) => Promise<PageResp<PartRecord>>;
  create: (values: PartFormValues, options: EmbodimentMutationOptions) => Promise<PartRecord>;
  update: (
    uuid: string,
    values: PartFormValues,
    options: EmbodimentMutationOptions,
  ) => Promise<PartRecord>;
  remove: (uuid: string, options: EmbodimentMutationOptions) => Promise<void>;
};

async function toRecordPage<Item extends PartItem>(
  response: PageResp<Item>,
): Promise<PageResp<PartRecord>> {
  return {
    items: response.items.map(toPartRecord),
    total: response.total,
    page: response.page,
    size: response.size,
  };
}

const partApis: Record<PartKind, PartApi> = {
  endEffector: {
    list: (query, options) => listEndEffectors(query, options).then(toRecordPage),
    create: (values, options) =>
      createEndEffector(
        {
          name: values.name.trim(),
          tag: normalizeOptionalText(values.tag),
          description: normalizeOptionalText(values.description),
        },
        options,
      ).then(toPartRecord),
    update: (uuid, values, options) =>
      updateEndEffector(
        uuid,
        {
          name: values.name.trim(),
          tag: normalizeOptionalText(values.tag),
          description: normalizeOptionalText(values.description),
        },
        options,
      ).then(toPartRecord),
    remove: deleteEndEffector,
  },
  tip: {
    list: (query, options) => listTips(query, options).then(toRecordPage),
    create: (values, options) =>
      createTip(
        {
          name: values.name.trim(),
          tag: normalizeOptionalText(values.tag),
          description: normalizeOptionalText(values.description),
        },
        options,
      ).then(toPartRecord),
    update: (uuid, values, options) =>
      updateTip(
        uuid,
        {
          name: values.name.trim(),
          tag: normalizeOptionalText(values.tag),
          description: normalizeOptionalText(values.description),
        },
        options,
      ).then(toPartRecord),
    remove: deleteTip,
  },
};

export function getPartApi(kind: PartKind): PartApi {
  return partApis[kind];
}
